import type { FC, ReactNode } from 'react';
import { Alert, AlertTitle } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { ExclamationIcon } from '@heroicons/react/solid';
import { Bookmark, Save, Trash2 } from 'lucide-react';

export type SavedViewsActionsMode =
  | { kind: 'activeView'; canSaveChanges: boolean; onSaveChanges: () => void; onRemoveView: () => void }
  | { kind: 'limitReached'; maxViews: number }
  | { kind: 'savePrompt'; onSaveView: () => void };

const Footer: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="mt-4 flex items-center justify-between border-t border-gray-300 pt-6">{children}</div>
);

export const SavedViewsActions: FC<{ mode: SavedViewsActionsMode }> = ({ mode }) => {
  switch (mode.kind) {
    case 'activeView':
      return (
        <Footer>
          <Button variant="primary" disabled={!mode.canSaveChanges} onClick={mode.onSaveChanges}>
            <Save aria-hidden="true" />
            Save changes
          </Button>
          <Button variant="outline" onClick={mode.onRemoveView}>
            <Trash2 aria-hidden="true" />
            Remove view
          </Button>
        </Footer>
      );
    case 'limitReached':
      return (
        <Footer>
          <Alert variant="warning" className="[&>svg]:text-warning-600 w-fit border-none">
            <ExclamationIcon aria-hidden="true" />
            <AlertTitle className="font-normal tracking-normal">
              You have reached the maximum of {mode.maxViews} saved views. Remove one to save another.
            </AlertTitle>
          </Alert>
        </Footer>
      );
    case 'savePrompt':
      return (
        <Footer>
          <Button
            variant="ghost"
            className="bg-primary-50 text-primary-700 hover:bg-primary-100 hover:text-primary-800"
            onClick={mode.onSaveView}
          >
            <Bookmark aria-hidden="true" />
            Save this view
          </Button>
        </Footer>
      );
  }
};
